import { useEffect, type CSSProperties } from "react";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";

const K = keys.celebrate;

/**
 * The moment a seller finishes a setup milestone themselves — their first
 * product, their first category.
 *
 * A toast was what this used to be, and a toast is what you show for "saved".
 * The first product is the thing the whole portal exists for; it earns a beat.
 *
 * Reuses the invite screen's pure-CSS timeline (`invite-*` in `styles.css`):
 * badge pop → check draw → ring pulse → confetti → text rise, with the
 * `prefers-reduced-motion` fallback already defined there (final state, no
 * motion). Gold rather than `--accent-*`: accent is the near-black action
 * colour in light mode, which would put a black badge on a dark scrim.
 */

const GOLD = "var(--gold-500)";
const GOLD_DARK = "var(--gold-600)";
const CREAM = "var(--canvas)";

type Spark = {
  size: number;
  color: string;
  dx: number;
  dy: number;
  delay: number;
  duration: number;
};

type Burst = { left: string; top: string; delay: number; radius: number };

/**
 * Several origins, fired in sequence across the screen — one central starburst
 * read as "a badge with bits around it", not as a celebration.
 */
const BURSTS: Burst[] = [
  { left: "22%", top: "28%", delay: 0.15, radius: 118 },
  { left: "76%", top: "24%", delay: 0.5, radius: 104 },
  { left: "50%", top: "68%", delay: 0.85, radius: 126 },
  { left: "30%", top: "72%", delay: 1.2, radius: 96 },
  { left: "70%", top: "58%", delay: 1.5, radius: 110 },
];

const SPARK_COLORS = [GOLD, CREAM, GOLD_DARK, GOLD];
const SPARKS_PER_BURST = 14;

/** Radial spray, with the vector biased downward so the sparks fall as they fade. */
function sparksFor(burst: Burst, index: number): Spark[] {
  return Array.from({ length: SPARKS_PER_BURST }, (_, i) => {
    const angle = (i / SPARKS_PER_BURST) * Math.PI * 2 + index * 0.4;
    // Alternating radius keeps the ring from reading as a perfect circle.
    const radius = burst.radius * (i % 2 === 0 ? 1 : 0.68);
    return {
      size: i % 3 === 0 ? 8 : 6,
      color: SPARK_COLORS[i % SPARK_COLORS.length],
      dx: Math.round(Math.cos(angle) * radius),
      dy: Math.round(Math.sin(angle) * radius + radius * 0.35),
      delay: burst.delay + (i % 4) * 0.02,
      duration: 1.05 + (i % 3) * 0.14,
    };
  });
}

const BADGE = 92;

export type FirstWin = "product" | "category" | "store";

const COPY: Record<FirstWin, { title: string; body: string }> = {
  product: { title: K.firstProductTitle, body: K.firstProductBody },
  category: { title: K.firstCategoryTitle, body: K.firstCategoryBody },
  store: { title: K.storeReadyTitle, body: K.storeReadyBody },
};

export function FirstWinCelebration({
  win,
  onClose,
}: {
  win: FirstWin;
  onClose: () => void;
}) {
  // Self-dismissing: the seller has already finished the thing, so an overlay
  // that waits to be acknowledged is one more chore at the end of the flow.
  // The button stays for anyone who wants it gone sooner.
  useEffect(() => {
    const timer = window.setTimeout(onClose, 5200);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const t = useTranslations();
  const copy = COPY[win];
  const check = Math.round(BADGE * 0.46);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[color:var(--color-hero-surface)]/92 px-6 text-center backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-label={t(copy.title)}
      onClick={onClose}
    >
      {/* Fireworks sit behind the badge and copy, across the whole screen. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {BURSTS.map((burst, b) => (
          <span
            key={b}
            className="absolute"
            style={{ left: burst.left, top: burst.top }}
          >
            <span
              className="tw-firework-flash absolute h-5 w-5 rounded-full bg-[color:var(--gold-500)]"
              style={{ margin: -10, animationDelay: `${burst.delay}s` }}
            />
            {sparksFor(burst, b).map((spark, i) => (
              <span
                key={i}
                className="tw-firework absolute rounded-full"
                style={
                  {
                    width: spark.size,
                    height: spark.size,
                    margin: -spark.size / 2,
                    background: spark.color,
                    "--dx": `${spark.dx}px`,
                    "--dy": `${spark.dy}px`,
                    animationDelay: `${spark.delay}s`,
                    animationDuration: `${spark.duration}s`,
                  } as CSSProperties
                }
              />
            ))}
          </span>
        ))}
      </div>

      <div className="relative flex flex-col items-center gap-5">
        <div className="relative" style={{ width: BADGE, height: BADGE }}>
          <span className="invite-ring absolute inset-0 rounded-full border-2 border-[color:var(--gold-500)]" />
          <div
            className="invite-badge absolute inset-0 flex items-center justify-center rounded-full bg-[color:var(--gold-500)]"
            style={{ boxShadow: "0 16px 44px rgba(250, 185, 91, 0.35)" }}
          >
            <svg width={check} height={check} viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <path
                d="M12 25 L20 33 L36 16"
                stroke="var(--color-hero-surface)"
                strokeWidth={5}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={48}
                className="invite-check"
              />
            </svg>
          </div>
        </div>

        <div
          className="invite-rise flex flex-col items-center gap-2"
          style={{ animationDelay: "0.85s" }}
        >
          <h2 className="m-0 max-w-[22ch] text-[22px] font-extrabold leading-tight text-[color:var(--canvas)]">
            {t(copy.title)}
          </h2>
          <p className="m-0 max-w-[34ch] text-[14px] leading-relaxed text-[color:var(--canvas)]/75">
            {t(copy.body)}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="invite-rise inline-flex h-11 items-center justify-center rounded-[11px] bg-[color:var(--gold-500)] px-6 text-[14px] font-bold text-[color:var(--color-hero-surface)]"
          style={{ animationDelay: "1s" }}
        >
          {t(K.continueLabel)}
        </button>
      </div>
    </div>
  );
}
